import React, { useState } from "react";
import { Modal, Button } from "react-bootstrap";
import moment from "moment";
import DefaultImageHandler from "../common/defaultImageHandler";

interface LeaveDetailsModalProps {
  show: boolean;
  onHide: () => void;
  leave: any | null;
}

const LeaveDetailsModal: React.FC<LeaveDetailsModalProps> = ({
  show,
  onHide,
  leave,
}) => {
  const [showFullReason, setShowFullReason] = useState(false);
  if (!leave) return null;
  const truncateReason = (text: string) => {
    const words = text.split(" ");
    return words.length <= 25 ? text : words.slice(0, 25).join(" ") + "...";
  };

  const toggleReason = () => setShowFullReason(!showFullReason);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <strong>Leave Details - {leave.leaveTypeName}</strong>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row g-3">
          {/* Left Column */}
          <div className="col-md-6 d-flex flex-column">
            <div className="card flex-fill shadow-sm p-3 border-0 h-100">
              <div className="d-flex align-items-center mb-3">
                <div className="avatar avatar-md me-3">
                  <DefaultImageHandler
                    name={leave.userName}
                    showRole={true}
                    id={leave.userId}
                    role="Employee"
                    showName={false}
                  />
                </div>
                <div>
                  <h5 className="mb-0">{leave.userName}</h5>
                  <small className="text-muted">Employee</small>
                </div>
              </div>

              <div className="mb-3">
                <h6>Status</h6>
                <div className="d-flex align-items-center">
                  <span
                    className={`badge px-3 py-2 bg-${
                      leave.status === "approved"
                        ? "success"
                        : leave.status === "pending"
                        ? "warning"
                        : "danger"
                    } text-uppercase`}
                  >
                    {leave.status}
                  </span>
                  {leave.approvalDate && (
                    <small className="text-muted ms-2">
                      on {moment(leave.approvalDate).format("DD-MMM-YYYY")}
                    </small>
                  )}
                </div>
              </div>

              {leave.status === "rejected" && leave.rejectionReason && (
                <>
                  <h6 className="text-danger">Rejection Reason</h6>
                  <div className="card p-2 bg-light border-start border-danger border-3">
                    {leave.rejectionReason}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Right Column */}
          <div className="col-md-6 d-flex flex-column">
            <div className="card flex-fill shadow-sm p-3 border-0 h-100">
              <h6>Leave Duration</h6>
              <div className="d-flex flex-wrap gap-3 mb-3">
                <div>
                  <div className="fw-semibold">From</div>
                  <div className="badge bg-primary-subtle text-dark">
                    {moment(leave.startDate).format("MMM D, YYYY")}
                  </div>
                </div>
                <div>
                  <div className="fw-semibold">To</div>
                  <div className="badge bg-primary-subtle text-dark">
                    {moment(leave.endDate).format("MMM D, YYYY")}
                  </div>
                </div>
                <div>
                  <div className="fw-semibold">Duration</div>
                  <div className="badge bg-info-subtle text-dark">
                    {leave.days} day(s)
                  </div>
                </div>
              </div>

              <h6>Request Date</h6>
              <div className="card p-2 bg-light border-0">
                {moment(leave.createdAt).format("MMM D, YYYY [at] h:mm A")}
              </div>
            </div>
          </div>

          {/* Reason Section */}
          <div className="col-md-6 d-flex flex-column">
            <div className="card flex-fill shadow-sm p-3 border-0 h-100">
              <div className="d-flex justify-content-between align-items-start">
                <h6>Reason</h6>
                {leave.reason && (
                  <button
                    onClick={toggleReason}
                    className="btn btn-link p-0 text-primary"
                  >
                    {showFullReason ? "View Less" : "View More"}
                  </button>
                )}
              </div>
              <div className="card p-2 bg-light border-0">
                {leave.reason ? (
                  showFullReason || leave.reason.split(" ").length <= 25
                    ? leave.reason
                    : truncateReason(leave.reason)
                ) : (
                  <span className="text-muted">No reason provided</span>
                )}
              </div>
            </div>
          </div>

          {/* Documents Section */}
          <div className="col-md-6 d-flex flex-column">
            <div className="card flex-fill shadow-sm p-3 border-0 h-100">
              <h6>Documents</h6>
              {leave.documents && leave.documents.length > 0 ? (
                <div className="d-flex flex-wrap gap-2">
                  {leave.documents.map((docStr: string, index: number) => {
                    const doc = JSON.parse(docStr);
                    return (
                      <a
                        key={doc?.public_id || index}
                        href={doc?.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-outline-secondary btn-sm d-flex align-items-center"
                      >
                        <i className="ti ti-file-text me-1"></i>
                        {doc.name || "Document"}
                      </a>
                    );
                  })}
                </div>
              ) : (
                <div className="card p-2 bg-light border-0">
                  <span className="text-muted">No documents attached</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="border-top-0">
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default LeaveDetailsModal;
